import type {
  FieldWorkRequest,
  FieldWorkRequestCreateInput,
  FieldWorkRequestPatch,
} from '../domain/fieldWorkRequest';
import type { FieldWorkStats } from '../domain/fieldWorkReporting';
import type { FieldOpsTeam } from '../domain/fieldOpsTeam';
import { fieldWorkApi as liveFieldWorkApi } from './live-field-work-api';
import { fieldWorkApi as mockFieldWorkApi } from './mock-field-work-api';

export interface FieldWorkListFilters {
  team?: FieldOpsTeam | undefined;
  status?: FieldWorkRequest['status'] | undefined;
  /** Exact assignee email, or the literal 'unassigned'. */
  assignee?: string | undefined;
}

/**
 * The Field Ops work queue, backed by leatherback's
 * FieldWorkRequestsService -- see domain/fieldWorkRequest.ts.
 */
export interface FieldWorkApi {
  listFieldWork(filters?: FieldWorkListFilters): Promise<FieldWorkRequest[]>;
  getFieldWork(id: string): Promise<FieldWorkRequest>;
  createFieldWork(input: FieldWorkRequestCreateInput): Promise<FieldWorkRequest>;
  updateFieldWork(patch: FieldWorkRequestPatch): Promise<FieldWorkRequest>;
  // Rewrites queue_position for a team in one atomic call. `ordered` is the
  // full top-to-bottom id list with the version each was loaded at.
  reorderFieldWorkQueue(
    team: FieldOpsTeam,
    ordered: { id: string; version: number }[],
  ): Promise<FieldWorkRequest[]>;
  /** Appends a note to the request's activity log. */
  addNote(id: string, note: string): Promise<FieldWorkRequest>;
  /** Assigns the request to someone else with a note for them, in one step. */
  handOff(id: string, version: number, toAssignee: string, note: string): Promise<FieldWorkRequest>;
  // Server-side aggregate for the reporting view. The live adapter falls
  // back to list-and-compute if the backend predates the RPC.
  getFieldWorkStats(team?: FieldOpsTeam): Promise<FieldWorkStats>;
}

export const fieldWorkApi: FieldWorkApi =
  import.meta.env.VITE_API_MODE === 'live' ? liveFieldWorkApi : mockFieldWorkApi;
